import authService, { TOKEN_TYPES } from "./authService";
import { toBackendTrialId } from "@/utils/trialIdMap";

export type MarkerSeverity = "Critical" | "Major" | "Minor";

export type DiscrepancyMarker = {
  open: number;
  new: number;
  max_severity: MarkerSeverity;
  /** Present on record-level markers: the table the record belongs to. */
  entity_table?: string | null;
};

export type MarkerLevel = "subject" | "visit" | "record";

export type MarkerMap = Record<string, DiscrepancyMarker>;

export type MarkerResponse = {
  level: MarkerLevel;
  markers: MarkerMap;
  enabled: boolean;
};

export type SubjectDiscrepancyItem = {
  discrepancy_id: string;
  issue_id: string | null;
  rule: string | null;
  severity: MarkerSeverity;
  detail: string | null;
  visit: string | null;
  source: string | null;
  detected_at: string | null;
  status: string;
  is_new: boolean;
};

export type SubjectDiscrepancySummary = {
  enabled: boolean;
  subject?: string;
  open: number;
  by_severity?: Record<string, number>;
  items: SubjectDiscrepancyItem[];
};

const base = (trialId: string) =>
  `${import.meta.env.VITE_API_URL}/data/trial/${toBackendTrialId(trialId)}/discrepancy_markers`;

async function request<T>(url: string): Promise<T> {
  const token = authService.getToken(TOKEN_TYPES.ACCESS) ?? "";
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Marker request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

const discrepancyMarkersApi = {
  getMarkers: (trialId: string, level: MarkerLevel) =>
    request<MarkerResponse>(`${base(trialId)}/?level=${level}`),

  getSubjectSummary: (trialId: string, subjectId: string) =>
    request<SubjectDiscrepancySummary>(
      `${base(trialId)}/subject/${encodeURIComponent(subjectId)}/`,
    ),

  getRecordDetail: (trialId: string, entityId: string) =>
    request<SubjectDiscrepancySummary>(
      `${base(trialId)}/record/${encodeURIComponent(entityId)}/`,
    ),
};

export default discrepancyMarkersApi;
